import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    PiUserBold,
    PiCaretDownBold,
    PiCaretUpBold,
    PiArrowBendDownRightBold,
    PiArrowSquareOutBold,
    PiMagnifyingGlassBold,
} from 'react-icons/pi';
import type {
    CommissionBreakdownItem,
    CommissionDealItem,
    CommissionOrgChartPerson,
    FunctionalOrgBranch,
    FunctionalOrgChartData,
    FunctionalOrgPillar,
} from './types';
import { fmtBrl, initials, personFirstName } from './orgChartUtils';

const RANKING_PREVIEW = 10;

function Avatar({
    person,
    size = 'md',
}: {
    person: CommissionOrgChartPerson;
    size?: 'sm' | 'md' | 'lg';
}) {
    const cls = {
        sm: 'size-8 text-xs',
        md: 'size-10 text-sm',
        lg: 'size-11 text-sm',
    }[size];

    if (person.avatar_url) {
        return (
            <img
                src={person.avatar_url}
                alt=""
                className={`${cls} shrink-0 rounded-full object-cover ring-1 ring-gray-200 dark:ring-zinc-600`}
            />
        );
    }

    return (
        <span className={`${cls} flex shrink-0 items-center justify-center rounded-full bg-gray-100 font-bold text-gray-600 ring-1 ring-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:ring-zinc-600`}>
            {initials(person.name)}
        </span>
    );
}

function BreakdownPanel({ breakdown, managerTotal }: { breakdown: CommissionBreakdownItem[]; managerTotal: number }) {
    return (
        <div className="space-y-1 border-t border-gray-100 bg-gray-50/80 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/50">
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400">Comissão recebida por fonte</p>
            {breakdown.map((item) => {
                const pct = managerTotal > 0 ? Math.round((item.total / managerTotal) * 100) : 0;
                return (
                    <div key={item.seller_id + item.seller_name} className="flex items-center gap-2">
                        <PiArrowBendDownRightBold className="size-3 shrink-0 text-gray-300" />
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-bold text-gray-600 dark:bg-zinc-700 dark:text-zinc-300">
                            {initials(item.seller_name)}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-[11px] font-semibold text-gray-700 dark:text-zinc-200">
                                {item.seller_name}
                                {item.is_own && (
                                    <span className="ml-1 rounded bg-gray-200 px-1 py-0.5 text-[9px] font-bold text-gray-600 dark:bg-zinc-600 dark:text-zinc-200">
                                        próprias
                                    </span>
                                )}
                            </p>
                            <p className="text-[10px] text-gray-400">{item.records_count} reg. · {pct}% do total</p>
                        </div>
                        <p className="shrink-0 text-xs font-bold text-emerald-700 dark:text-emerald-400">{fmtBrl(item.total)}</p>
                    </div>
                );
            })}
        </div>
    );
}

function DealsDropdown({ deals }: { deals: CommissionDealItem[] }) {
    return (
        <div className="space-y-1 border-t border-gray-100 bg-gray-50/80 px-2 py-1.5 dark:border-zinc-700 dark:bg-zinc-800/40">
            {deals.map((deal) => (
                <Link
                    key={deal.deal_id}
                    to={`/commissioning/deals/${deal.deal_id}`}
                    className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] hover:bg-white dark:hover:bg-zinc-800"
                >
                    <PiArrowBendDownRightBold className="size-3 shrink-0 text-gray-300 group-hover:text-orange-400" />
                    <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-700 group-hover:text-gray-900 dark:text-zinc-300">
                            #{deal.deal_number} · {deal.title}
                        </p>
                        {deal.finish_date && (
                            <p className="text-[10px] text-gray-400">{deal.finish_date}</p>
                        )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        <span className="font-bold text-emerald-700 dark:text-emerald-400">{fmtBrl(deal.final_amount)}</span>
                        <PiArrowSquareOutBold className="size-3 text-gray-300 group-hover:text-orange-400" />
                    </div>
                </Link>
            ))}
        </div>
    );
}

function PersonRow({ person }: { person: CommissionOrgChartPerson }) {
    const [dealsOpen, setDealsOpen] = useState(false);
    const isZero = person.total === 0;
    const hasDeals = (person.deals?.length ?? 0) > 0;
    return (
        <div className="overflow-hidden rounded-lg border border-transparent hover:border-gray-100 dark:hover:border-zinc-700">
            <Link
                to={`/commissioning/sellers/${person.user_id}`}
                className="flex items-center gap-2.5 px-2 py-2 transition hover:bg-gray-50 dark:hover:bg-zinc-800/60"
            >
                <Avatar person={person} size="sm" />
                <div className="min-w-0 flex-1">
                    <p className={`truncate text-xs font-semibold ${isZero ? 'text-gray-400' : 'text-gray-900 dark:text-zinc-100'}`}>
                        {person.name}
                    </p>
                    <p className="truncate text-[11px] text-gray-400">{person.role_label}</p>
                </div>
                <div className="shrink-0 text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Comissão</p>
                    <p className={`text-xs font-bold ${isZero ? 'text-gray-300' : 'text-emerald-700 dark:text-emerald-400'}`}>
                        {fmtBrl(person.total)}
                    </p>
                </div>
            </Link>

            {hasDeals && (
                <button
                    type="button"
                    onClick={() => setDealsOpen((v) => !v)}
                    className="flex w-full items-center gap-1.5 border-t border-gray-100 bg-gray-50/80 px-3 py-1 text-[10px] font-semibold text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/40"
                >
                    {dealsOpen ? <PiCaretUpBold className="size-2.5" /> : <PiCaretDownBold className="size-2.5" />}
                    {dealsOpen
                        ? 'Ocultar vendas'
                        : `Ver ${person.deals!.length} venda${person.deals!.length !== 1 ? 's' : ''} · ${fmtBrl(person.total)}`}
                </button>
            )}
            {dealsOpen && hasDeals && <DealsDropdown deals={person.deals!} />}
        </div>
    );
}

type RankingRow = {
    person: CommissionOrgChartPerson;
    teamName: string;
};

function SellerRanking({
    branches,
    pillars = [],
    unassigned,
}: {
    branches: FunctionalOrgBranch[];
    pillars?: FunctionalOrgPillar[];
    unassigned: CommissionOrgChartPerson[];
}) {
    const [query, setQuery] = useState('');
    const [showAll, setShowAll] = useState(false);

    const rows = useMemo(() => {
        const map = new Map<number, RankingRow>();

        for (const branch of branches) {
            for (const member of branch.members) {
                map.set(member.user_id, { person: member, teamName: branch.team_name });
            }
        }

        for (const pillar of pillars) {
            for (const person of pillar.people) {
                if (!map.has(person.user_id)) {
                    map.set(person.user_id, { person, teamName: pillar.label });
                }
            }
        }

        for (const person of unassigned) {
            if (!map.has(person.user_id)) {
                map.set(person.user_id, { person, teamName: 'Sem time' });
            }
        }

        return Array.from(map.values()).sort((a, b) => b.person.total - a.person.total);
    }, [branches, pillars, unassigned]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return rows;
        }
        return rows.filter(
            (r) =>
                r.person.name.toLowerCase().includes(q) ||
                r.teamName.toLowerCase().includes(q),
        );
    }, [rows, query]);

    const visible = showAll ? filtered : filtered.slice(0, RANKING_PREVIEW);
    const hasMore = filtered.length > RANKING_PREVIEW;

    if (rows.length === 0) {
        return null;
    }

    return (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:border-zinc-700">
                <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Ranking de vendedores</h2>
                    <p className="text-xs text-gray-400">
                        {filtered.length} vendedor{filtered.length !== 1 ? 'es' : ''} · ordenado por comissão
                    </p>
                </div>
                <label className="relative block w-full sm:w-64">
                    <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setShowAll(false);
                        }}
                        placeholder="Buscar por nome ou time…"
                        className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-100 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
                    />
                </label>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                    <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:bg-zinc-800/80">
                        <tr>
                            <th className="px-4 py-2.5 font-semibold">#</th>
                            <th className="px-4 py-2.5 font-semibold">Vendedor</th>
                            <th className="px-4 py-2.5 font-semibold">Time</th>
                            <th className="px-4 py-2.5 font-semibold text-right">Registros</th>
                            <th className="px-4 py-2.5 font-semibold text-right">Comissão</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {visible.map((row, i) => {
                            const rank = i + 1;
                            const isZero = row.person.total === 0;
                            return (
                                <tr key={row.person.user_id} className="hover:bg-gray-50/80 dark:hover:bg-zinc-800/40">
                                    <td className="px-4 py-2.5 tabular-nums text-gray-400">{rank}</td>
                                    <td className="px-4 py-2.5">
                                        <Link
                                            to={`/commissioning/sellers/${row.person.user_id}`}
                                            className="flex items-center gap-2.5 font-medium text-gray-900 hover:text-orange-700 dark:text-zinc-100"
                                        >
                                            <Avatar person={row.person} size="sm" />
                                            <span className="min-w-0 truncate">{row.person.name}</span>
                                        </Link>
                                    </td>
                                    <td className="px-4 py-2.5 text-gray-500 dark:text-zinc-400">{row.teamName}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                                        {row.person.records_count}
                                    </td>
                                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${isZero ? 'text-gray-300' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                        {fmtBrl(row.person.total)}
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                                    Nenhum vendedor encontrado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {hasMore && !showAll && (
                <div className="border-t border-gray-100 px-4 py-3 dark:border-zinc-700">
                    <button
                        type="button"
                        onClick={() => setShowAll(true)}
                        className="text-xs font-semibold text-orange-700 hover:underline dark:text-orange-400"
                    >
                        Ver todos os vendedores ({filtered.length})
                    </button>
                </div>
            )}
            {showAll && hasMore && (
                <div className="border-t border-gray-100 px-4 py-3 dark:border-zinc-700">
                    <button
                        type="button"
                        onClick={() => setShowAll(false)}
                        className="text-xs font-semibold text-gray-500 hover:underline"
                    >
                        Mostrar só o top {RANKING_PREVIEW}
                    </button>
                </div>
            )}
        </section>
    );
}

function TeamAccordion({
    branch,
    open,
    onToggle,
}: {
    branch: FunctionalOrgBranch;
    open: boolean;
    onToggle: () => void;
}) {
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const isZero = branch.team_total === 0;
    const hasBreakdown = (branch.manager.breakdown?.length ?? 0) > 0;

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/60"
            >
                <Avatar person={branch.manager} size="md" />
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-zinc-100">
                        {branch.team_name}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                        {personFirstName(branch.manager.name)} · {branch.members.length} vendedor
                        {branch.members.length !== 1 ? 'es' : ''}
                    </p>
                </div>
                <div className="hidden shrink-0 text-right sm:block">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Volume</p>
                    <p className={`text-sm font-semibold ${isZero ? 'text-gray-300' : 'text-gray-800 dark:text-zinc-100'}`}>
                        {fmtBrl(branch.team_total)}
                    </p>
                </div>
                {branch.manager.total > 0 && (
                    <div className="hidden shrink-0 text-right md:block">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Gerente</p>
                        <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                            {fmtBrl(branch.manager.total)}
                        </p>
                    </div>
                )}
                {open ? (
                    <PiCaretUpBold className="size-4 shrink-0 text-gray-400" />
                ) : (
                    <PiCaretDownBold className="size-4 shrink-0 text-gray-400" />
                )}
            </button>

            {open && (
                <div className="border-t border-gray-100 dark:border-zinc-700">
                    <div className="flex items-center justify-between gap-2 border-b border-gray-50 px-4 py-2 dark:border-zinc-800">
                        <Link
                            to={`/commissioning/sellers/${branch.manager.user_id}`}
                            className="text-xs font-medium text-orange-700 hover:underline dark:text-orange-400"
                        >
                            Ver perfil do gerente
                        </Link>
                        {hasBreakdown && (
                            <button
                                type="button"
                                onClick={() => setBreakdownOpen((v) => !v)}
                                className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                            >
                                {breakdownOpen ? 'Ocultar detalhamento' : 'De onde veio a comissão'}
                            </button>
                        )}
                    </div>

                    {breakdownOpen && hasBreakdown && (
                        <BreakdownPanel
                            breakdown={branch.manager.breakdown!}
                            managerTotal={branch.manager.total}
                        />
                    )}

                    <div className="space-y-0.5 p-1.5">
                        {branch.members.length === 0 ? (
                            <div className="flex flex-col items-center gap-1 py-6 text-center">
                                <PiUserBold className="size-5 text-gray-300" />
                                <p className="text-xs text-gray-400">Sem vendedores</p>
                            </div>
                        ) : (
                            branch.members.map((member) => (
                                <PersonRow key={member.user_id} person={member} />
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function PillarAccordion({ pillar }: { pillar: FunctionalOrgPillar }) {
    const [open, setOpen] = useState(false);
    const isZero = pillar.total === 0;

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/60"
            >
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-zinc-100">{pillar.label}</p>
                    {pillar.rate_hint && (
                        <p className="text-[11px] text-gray-400">{pillar.rate_hint}</p>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <p className={`text-sm font-semibold ${isZero ? 'text-gray-300' : 'text-emerald-700 dark:text-emerald-400'}`}>
                        {fmtBrl(pillar.total)}
                    </p>
                    {open ? (
                        <PiCaretUpBold className="size-4 text-gray-400" />
                    ) : (
                        <PiCaretDownBold className="size-4 text-gray-400" />
                    )}
                </div>
            </button>
            {open && (
                <div className="space-y-0.5 border-t border-gray-100 p-1.5 dark:border-zinc-700">
                    {pillar.people.length === 0 ? (
                        <div className="flex flex-col items-center gap-1 py-6 text-center">
                            <PiUserBold className="size-5 text-gray-300" />
                            <p className="text-xs text-gray-400">Sem comissões no período</p>
                        </div>
                    ) : (
                        pillar.people.map((p) => <PersonRow key={p.user_id} person={p} />)
                    )}
                </div>
            )}
        </div>
    );
}

export default function CompactOrgChartOverview({
    data,
    unassigned = [],
}: {
    data: FunctionalOrgChartData;
    unassigned?: CommissionOrgChartPerson[];
}) {
    const [directorExpanded, setDirectorExpanded] = useState(false);
    const sortedBranches = useMemo(
        () => [...data.manager_branches].sort((a, b) => b.team_total - a.team_total),
        [data.manager_branches],
    );
    const [openTeamId, setOpenTeamId] = useState<number | null>(null);
    const hasDirectorBreakdown = (data.president?.breakdown?.length ?? 0) > 0;

    useEffect(() => {
        setOpenTeamId(sortedBranches[0]?.manager.user_id ?? null);
        setDirectorExpanded(false);
        // Abre o time de maior volume ao trocar período/dados.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage à lista de times
    }, [data.manager_branches]);

    return (
        <div className="space-y-5">
            {/* Diretor — largura total, compacto */}
            {data.president ? (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                    <Link
                        to={`/commissioning/sellers/${data.president.user_id}`}
                        className="flex items-center gap-3 px-4 py-3 transition hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                    >
                        <Avatar person={data.president} size="lg" />
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                                Diretor Comercial
                            </p>
                            <p className="truncate text-sm font-semibold text-gray-900 dark:text-zinc-100">
                                {data.president.name}
                            </p>
                            {data.president.rate_hint && (
                                <p className="truncate text-xs text-gray-500">{data.president.rate_hint}</p>
                            )}
                        </div>
                        <div className="shrink-0 text-right">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Comissão</p>
                            <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">
                                {fmtBrl(data.president.total)}
                            </p>
                            <p className="text-xs text-gray-400">{data.president.records_count} registro(s)</p>
                        </div>
                    </Link>

                    {hasDirectorBreakdown && (
                        <button
                            type="button"
                            onClick={() => setDirectorExpanded((v) => !v)}
                            className="flex w-full items-center gap-1.5 border-t border-gray-100 px-4 py-2 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800/50"
                        >
                            {directorExpanded ? <PiCaretUpBold className="size-3" /> : <PiCaretDownBold className="size-3" />}
                            {directorExpanded ? 'Ocultar detalhamento' : 'Ver composição da comissão'}
                        </button>
                    )}

                    {directorExpanded && hasDirectorBreakdown && (
                        <BreakdownPanel
                            breakdown={data.president.breakdown!}
                            managerTotal={data.president.total}
                        />
                    )}
                </div>
            ) : (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 px-6 py-5 text-sm text-gray-400 dark:border-zinc-600">
                    <PiUserBold className="size-4" />
                    Diretor não cadastrado
                </div>
            )}

            {/* Ranking — bloco principal */}
            <SellerRanking
                branches={data.manager_branches}
                pillars={data.pillars}
                unassigned={unassigned}
            />

            {/* Times — accordion */}
            <section className="space-y-3">
                <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Equipes comerciais</h2>
                    <p className="text-xs text-gray-400">Expanda um time para ver vendedores e detalhamento</p>
                </div>

                {sortedBranches.length > 0 ? (
                    <div className="space-y-2">
                        {sortedBranches.map((branch) => (
                            <TeamAccordion
                                key={branch.manager.user_id}
                                branch={branch}
                                open={openTeamId === branch.manager.user_id}
                                onToggle={() =>
                                    setOpenTeamId((cur) =>
                                        cur === branch.manager.user_id ? null : branch.manager.user_id,
                                    )
                                }
                            />
                        ))}
                    </div>
                ) : (
                    <p className="rounded-xl border border-dashed border-gray-300 px-6 py-4 text-center text-sm text-gray-400 dark:border-zinc-600">
                        Nenhum gerente comercial cadastrado.
                    </p>
                )}
            </section>

            {/* Pilares — accordion secundário */}
            {data.pillars.length > 0 && (
                <section className="space-y-3">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Outros pilares</h2>
                        <p className="text-xs text-gray-400">SDR, overrides e demais papéis do período</p>
                    </div>
                    <div className="space-y-2">
                        {data.pillars.map((pillar) => (
                            <PillarAccordion key={pillar.label} pillar={pillar} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
